<template>
  <div>

    <div v-if="$route.params.invitationId" class="d-flex justify-end">
      <audit-trail-button entity-type="MemberInvitation" :entity-id="$route.params.invitationId" />
    </div>

    <div class="member-wrapper" v-if="invitation.id">

      <!-- Breadcrumb -->
      <v-breadcrumbs
          class="mt-3 level-2"
          :items="[
              {text: $tc('member.member', 2), to: {name: 'organizations.members'}, exact: true},
              {text: $t('member.invitation.manage'), to: {name: 'organizations.members.invitations'}, exact: true},
              {text:  invitationName},
          ]"
          divider=">"
      ></v-breadcrumbs>

      <!-- Infos générales -->

      <headline>
        {{ $tc("organization.general-information") }}
      </headline>

      <div class="grid">
        <v-form ref="form" @submit.prevent="save">

          <v-row>
            <v-col class="label" sm="3">
              {{ $t('status') }} :
            </v-col>
            <v-col>
              {{ invitation.status ? $t('member.status.' + invitation.status) : '-' }}
              <v-btn
                  v-if="isAlreadySent"
                  small
                  text
                  outlined
                  :href="`/member/invitation/${invitation.invitationToken}`"
                  target="_blank"
                  class="ml-5"
              >
                {{ $t('member.invitation.invitation-link') }}
              </v-btn>


            </v-col>

            <v-col cols="auto">

              <v-btn class="ml-5" small @click="resendInvitation" v-if="invitation.status === 'PENDING'">
                <v-icon left>mdi-refresh</v-icon>
                {{ $t('member.invitation.resend') }}
              </v-btn>
            </v-col>
          </v-row>

          <!-- Type -->
          <v-row>
            <v-col class="label" sm="3">
              {{ $t('type') }}
            </v-col>
            <v-col sm="3">
              <v-select
                  v-model="invitation.memberType.id"
                  :items="memberTypes"
                  item-text="name"
                  item-value="id"
                  hide-details
                  clearable
              ></v-select>
            </v-col>
          </v-row>

          <!-- First name -->
          <v-row>
            <v-col class="label" sm="3">
              {{ $t('firstname') }}
            </v-col>
            <v-col class="label" sm="3">
              <v-text-field
                  v-model="invitation.firstname"
                  hide-details
              />
            </v-col>

            <v-col class="label" sm="3">
              {{ $t('lastname') }}
            </v-col>
            <v-col class="label" sm="3">
              <v-text-field
                  v-model="invitation.lastname"
                  hide-details
              />
            </v-col>
          </v-row>

          <!-- Matricule -->
          <v-row>
            <v-col class="label" sm="3">
              {{ $t('member.external-reference') }}
            </v-col>
            <v-col class="label" sm="3">
              <v-text-field
                  v-model="invitation.externalReference"
                  hide-details
              />
            </v-col>

            <v-col class="label" sm="3">
              {{ $t('vehicle-registration') }}
            </v-col>
            <v-col class="label" sm="3">
              <vehicle-default-selector
                  v-model="invitation.vehiclePayload"
                  :vehicles="invitation.vehicles || []"
                  :initial-vehicle-id="invitation.defaultVehicleId || null"
                  :disabled="!invitationOrgAndCustomerSet"
                  :placeholder="$t('vehicle-registration')"
              />
            </v-col>
          </v-row>



          <custom-fields
              v-if="customFields"
              :fields="customFields"
              v-model="invitation.customFields"
              :col-field="9"
          />

          <!-- Crée le... par... -->
          <v-row>
            <v-col class="label" sm="3">
              {{ $t('created-at') }} :
            </v-col>

            <v-col>
              {{ invitation.createdAt ? formatDate(invitation.createdAt) : '-' }}
            </v-col>

            <v-col class="label" sm="3">
              {{ $t('member.last-sent-at') }} :
            </v-col>
            <v-col>
              {{ invitation.lastSentAt ? formatDate(invitation.lastSentAt) : '-' }}
            </v-col>
          </v-row>

          <!-- Groupes -->

          <headline class="mt-16">
            {{ $tc("member-group.title") }}
          </headline>

          <v-row>
            <v-col class="label" sm="3">
              {{ $tc('member-group.title', 2) }}
            </v-col>
            <v-col sm="3">
              <v-select
                  v-model="invitation.groups"
                  :items="groups"
                  item-text="name"
                  item-value="id"
                  hide-details
                  multiple
                  chips
                  clearable
              >
                <template #selection="{ item }">
                  <v-chip dark outlined :color="getOrganizationGroupTypeColor(item.type)">
                    {{ item.name }}
                  </v-chip>
                </template>
              </v-select>
            </v-col>
          </v-row>

          <!-- Badges -->
          <template>
            <headline class="mt-16">
              {{ $tc("member.token", 2) }}
            </headline>

            <v-row>
              <v-col class="label" sm="3">
                {{ $tc("member.token", 1) }}
              </v-col>
              <v-col sm="3">
                <!-- Autocomplete branché sur la recherche SERVEUR : le v-select ne filtrait
                     que les 20 badges déjà chargés, et sur `name` — or la plupart des badges
                     n'ont pas de nom, donc saisir un UID ne trouvait rien, même parmi ces 20. -->
                <v-autocomplete
                    v-model="invitation.tokenId"
                    :items="tokenItems"
                    :loading="organizationTokensLoading"
                    :search-input.sync="tokenSearch"
                    item-text="searchLabel"
                    item-value="id"
                    no-filter
                    hide-details
                    clearable
                    :placeholder="$t('member.token-search-placeholder')"
                    :no-data-text="tokenSearch ? $t('member.token-no-result') : $t('member.token-type-to-search')"
                >
                  <template #item="{ item, on, attrs }">
                    <v-list-item v-on="on" v-bind="attrs">
                      <v-list-item-content>
                        <v-list-item-title>
                          <div>
                            {{ item.uid }} <span v-if="item.name">({{ item.name }})</span>
                          </div>
                        </v-list-item-title>
                        <v-list-item-subtitle v-if="item.member && (item.member.firstname || item.member.lastname)">
                          Lié à : {{ item.member.firstname }} {{ item.member.lastname }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                  <!-- Une simple ligne de texte, pas un v-list-item : celui-ci est un bloc de
                       48 px avec ses propres marges, qui monopolisait la ligne et poussait le
                       champ de saisie en dessous — d'où la hauteur qui doublait à l'ouverture.
                       La sélection et l'input doivent tenir côte à côte. -->
                  <template #selection="{ item }">
                    <span class="token-selection">
                      {{ item.uid }}<span v-if="item.name"> ({{ item.name }})</span>
                    </span>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>
          </template>

          <!-- Submit -->
          <v-row>
            <v-col class="justify-end">
              <v-btn
                  color="primary"
                  type="submit"
                  :disabled="!isFormValid"
                  class="mr-12"
              >
                {{ isAlreadySent ? $t('update') : $t('member.invitation.send') }}
              </v-btn>
            </v-col>
          </v-row>


        </v-form>
      </div>
    </div>
  </div>
</template>

<script>
import AuditTrailButton from "@elements/AuditTrailButton.vue";

import MemberInvitationRepository from "@repository/MemberInvitationRepository";
import CustomFields from "@blocks/CustomFields.vue";
import MemberTypeRepository from "@repository/MemberTypeRepository";
import MemberGroupRepository from "@repository/MemberGroupRepository";
import TokenRepository from "@repository/TokenRepository";
import VehicleDefaultSelector from "@blocks/Select/VehicleDefaultSelector.vue";

export default {
  components: {
    AuditTrailButton,
    CustomFields,
    VehicleDefaultSelector,
  },

  data() {
    return {
      invitation: {
        id: null,
        memberType: {
          id: null,
        },
        tokenId: null,
        defaultVehicleId: null,
        vehicles: [],
        vehiclePayload: {},
        externalReference: null,
        groups: [],
        customFields: [],
        lastSentAt: null,
      },
      memberTypes: [],
      groups: [],
      organizationTokens: [],
      organizationTokensLoading: false,
      tokenSearch: null,
      tokenSearchTimer: null,
      tokenSearchUsed: false,
      // Le badge déjà rattaché à l'invitation : conservé à part pour rester sélectionnable
      // même quand la recherche courante ne le ramène pas.
      selectedToken: null,
      customFields: [],
      isFormValid: false,
      typesLoaded: false,
    }
  },


  mounted() {
    if(!this.currentUser.permissions.includes('ORGANIZATION_READ')) {
      this.$router.replace({name: 'dashboard'})
      return
    }

    this.loadData()
  },

  computed: {
    /**
     * Les badges proposés. Le libellé sert à l'affichage de l'input (`item-text`) : il est
     * bâti sur l'uid, toujours présent, alors que `name` est souvent vide.
     *
     * Le badge déjà sélectionné est épinglé en tête même si la recherche courante ne le
     * ramène pas — sans ça, ouvrir une invitation existante affichait un champ vide et le
     * moindre enregistrement aurait détaché le badge.
     */
    tokenItems() {
      const label = t => (t.name ? `${t.uid} (${t.name})` : t.uid)
      const items = this.organizationTokens.map(t => ({ ...t, searchLabel: label(t) }))
      if (this.selectedToken && !items.some(t => t.id === this.selectedToken.id)) {
        items.unshift({ ...this.selectedToken, searchLabel: label(this.selectedToken) })
      }
      return items
    },

    invitationId() {
      return this.$route.params.invitationId
    },

    isAlreadySent() {
      return this.invitation.lastSentAt != null
    },

    invitationName() {
      if(this.invitation.firstname === null) {
        return this.invitation.lastname ?? ''
      }
      return this.invitation.lastname ? this.invitation.firstname + ' ' + this.invitation.lastname : this.invitation.firstname
    },

    invitationOrgAndCustomerSet() {
      return !!(this.invitation.organization && this.invitation.customer)
    },
  },

  watch: {
    // Recherche serveur débouncée : on ne veut pas une requête par frappe, mais on veut que
    // la saisie cherche vraiment dans TOUS les badges, pas dans la page déjà chargée.
    tokenSearch(value) {
      const selectedLabel = this.tokenItems.find(t => t.id === this.invitation.tokenId)?.searchLabel
      // Vuetify réémet le libellé de l'élément choisi juste après la sélection : ce n'est pas
      // une frappe de l'utilisateur, et relancer dessus ferait clignoter la liste.
      if (value === selectedLabel) {
        return
      }
      // Au montage, Vuetify émet une valeur sur search-input avant toute frappe : sans ce
      // garde-fou, l'écran partait sur une requête en double avec le chargement initial.
      if (!value && !this.tokenSearchUsed) {
        return
      }
      this.tokenSearchUsed = true
      clearTimeout(this.tokenSearchTimer)
      this.tokenSearchTimer = setTimeout(() => this.loadTokens(value), 300)
    },

    'invitation.memberType.id': {
      handler(val, oldVal) {
        if(oldVal) {
          this.invitation.customFields = []
        }
        this.loadCustomFields()
      },
      immediate: true
    },

    invitation: {
      handler() {
        this.checkForm()
      },
      deep: true,
    },
  },

  methods: {
    checkForm() {
      this.isFormValid = this.invitation?.firstname?.trim() && this.invitation?.lastname?.trim()
    },

    save() {
      if(!this.isFormValid) {
        return
      }
      this.showLoading(true)
      let data = this.cloneObj(this.invitation, ['customFields', 'externalReference', 'lastname', 'firstname', 'groups', 'tokenId'])
      data.memberTypeId = this.invitation.memberType.id

      if (this.invitation.vehiclePayload && Object.keys(this.invitation.vehiclePayload).length) {
        Object.assign(data, this.invitation.vehiclePayload)
      }

      MemberInvitationRepository.update(this.invitation.id, data).then(() => {
        if(this.invitation.lastSentAt) {
          this.notifySuccess(this.$t('update-success'))
        } else {
          this.notifySuccess(this.$t('member.invitation.sent'))
        }
        this.loadData()
      }).catch(err => {
        this.showLoading(false)
        this.notifyError(err)
      })
    },

    loadData() {
      this.showLoading(true)
      MemberInvitationRepository.get(this.invitationId).then(data => {
        if(data.memberType === null) {
          data.memberType = {
            id: null,
          }
        }
        this.invitation = { ...data, vehiclePayload: {} }
        this.checkForm()
        this.loadMemberTypes()
        this.loadGroups()
        this.loadTokens()
        this.loadSelectedToken()
        this.showLoading(false)
      }).catch(err => {
        this.showLoading(false)

        this.notifyError(err)

        if(err.response.data.message === 'organization.member.invitation.errors.not-found') {
          this.$router.replace({name: 'organizations.members.invitations'})
        }
      })
    },

    loadMemberTypes() {
      let query = {
        organizationId: this.invitation.organization.id
      }
      MemberTypeRepository.search(query).then(response => {
        let data = response.items;
        this.memberTypes = []
        for(let i = 0; i < data.length; i++) {
          this.memberTypes.push({
            id: data[i].id,
            name: data[i].name,
            customFields: data[i].customFields
          })
        }

        this.loadCustomFields()

        this.typesLoaded = true
      })
    },

    loadGroups() {
      let data = {
        organizationId: this.invitation.organization.id
      }

      MemberGroupRepository.search(data).then(response => {
        this.groups = response.items
      }).catch(e => {
        this.notifyError(e)
      })
    },

    loadTokens(searchText = null) {

      this.organizationTokensLoading = true
      // 100 plutôt que le défaut serveur de 20 : sans recherche, une organisation qui a
      // quelques dizaines de badges les voit tous d'un coup. Au-delà, c'est la saisie qui
      // filtre — côté serveur, sur l'uid, le nom, la référence physique, le client et
      // l'organisation.
      let data = {
        organizationId: this.invitation.organization.id,
        limit: 100,
      }
      if (searchText) {
        data.searchText = searchText
      }

      TokenRepository.search(data).then(response => {
        this.organizationTokens = response.items
        this.organizationTokensLoading = false
      }).catch(e => {
        this.organizationTokensLoading = false
        // Une recherche annulée (frappe suivante) n'est pas une erreur à montrer.
        if (e?.code !== 'ERR_CANCELED' && e?.name !== 'CanceledError') {
          this.notifyError(e)
        }
      })
    },

    /**
     * Charge le badge déjà rattaché pour qu'il reste affiché et sélectionné, même s'il ne fait
     * pas partie de la page courante de résultats.
     */
    loadSelectedToken() {
      const id = this.invitation.tokenId
      if (!id || this.selectedToken?.id === id) {
        return
      }
      TokenRepository.get(id)
        .then(token => { this.selectedToken = token })
        .catch(() => { /* badge supprimé entre-temps : le champ reste vide, sans bruit */ })
    },

    loadCustomFields() {
      const selectedType = this.memberTypes.find(type => type.id === this.invitation.memberType?.id);
      if (selectedType) {
        this.customFields = selectedType.customFields;
      }
    },

    resendInvitation() {
      const id = this.invitation.id

      this.showLoading(true)
      MemberInvitationRepository.resendMemberInvitation(id).then(() => {
        this.loadData()
        this.showLoading(false)
        this.$dialog.notify.success(this.$t('member.invitation.resend'))
      }).catch(e => {
        this.showLoading(false)
        this.notifyError(e)
      })
    },
  }
}
</script>

<style lang="scss">
// La sélection reste sur une seule ligne, à côté du champ de saisie : elle ne doit ni
// pousser l'input en dessous, ni faire grandir la ligne quand l'uid est long.
.token-selection {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.member-wrapper {

  .no-border-bottom {
    border-bottom: none !important;
  }
}
</style>
